import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useCareCreditConnectProvider } from '@frontend/payments-care-credit';
import { theme } from '@frontend/theme';
import { Button, Text, useAlert, useModalControl } from '@frontend/design-system';
import { ConenctProviderFormValues } from './connect-provider-form';
import { ConnectProviderModal } from './connect-provider-modal';
import { ThingsToKnowModal } from './things-to-know-modal';

const styles = {
  heading: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
    margin-bottom: var(--fabric-space-padding-xs);
  `,
  divider: css`
    margin: var(--fabric-space-padding-md) 0;
    border-top-color: var(--fabric-color-neutral-20);
    border-width: 1px;
  `,
  buttonRow: css`
    display: flex;
    gap: var(--fabric-space-gap-xs);
    align-items: center;
  `,
};

export type LocationNotConnectedSettingsProps = {
  locationId: string | undefined;
};
export const LocationNotConnectedSettings = ({ locationId }: LocationNotConnectedSettingsProps) => {
  const { t } = useTranslation('payments');
  const { triggerProps: connectAccountTriggerProps, modalProps: connectAccountModalProps } = useModalControl();

  const { triggerProps: thingsToKnowTriggerProps, modalProps: thingsToKnowModalProps } = useModalControl();

  const alert = useAlert();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { onSaveMutation, isMutating } = useCareCreditConnectProvider({
    locationId,
  });

  const onConnectProvider = useCallback(
    async (values: ConenctProviderFormValues) => {
      setErrorMessage(null);

      const result = await onSaveMutation({
        merchantNumber: values.merchantNumber,
        last4Digits: values.validationMethod === 'last4' ? values.validationCode : undefined,
        practiceCode: values.validationMethod === 'practice' ? values.validationCode : undefined,
      });

      if (result.id) {
        connectAccountModalProps.onClose();

        alert.success(t('CareCredit account connected successfully'));
      } else {
        if (result.errorResponse) {
          alert.error(t('Failed to connect CareCredit account.'));

          if (!!result.errorResponse.errors?.length) {
            setErrorMessage(
              t('Merchant ID or validation method are incorrect. Please verify your information and try again.')
            );
          } else if (result.errorResponse.errorMessage) {
            setErrorMessage(result.errorResponse.errorMessage);
          }
        } else {
          alert.error(t('Failed to connect CareCredit account. Please try again.'));
        }
      }
    },
    [onSaveMutation]
  );

  return (
    <div className='location-connected-settings'>
      <div className='heading' css={styles.heading}>
        <div>
          <Icon name='data-sync' size={16} />
        </div>
        <div>
          <Text color='default' size='large' weight='bold'>
            <Trans t={t}>Connect an existing CareCredit Provider account</Trans>
          </Text>
        </div>
      </div>
      <Text size='small' css={{ marginBottom: theme.spacing(1) }}>
        <Trans t={t}>
          You'll need your Merchant ID and last four digits of your bank account OR your practice code to connect your
          account.
        </Trans>
      </Text>
      <div css={styles.buttonRow}>
        <Button iconName='data-sync' {...connectAccountTriggerProps}>
          {t('Connect Account')}
        </Button>
        <Button iconName='info' variant='secondary' {...thingsToKnowTriggerProps}>
          {t('Things to Know')}
        </Button>
      </div>
      <hr css={styles.divider} />
      <Text size='small' weight='bold'>
        {t(`Don't have an account?`)}
      </Text>
      <Text size='small'>
        <Trans t={t}>
          Become an{' '}
          <a
            href='https://www.carecredit.com/providers/contact-team/?sitecode=1jdasemla1&partner=Weave'
            target='_blank'
            css={{ color: theme.colors.primary50 }}
            rel='noreferrer'
          >
            enrolled CareCredit Provider
          </a>
          .
        </Trans>
      </Text>

      <ConnectProviderModal
        onSubmit={onConnectProvider}
        isMutating={isMutating}
        modalProps={connectAccountModalProps}
        errorMessage={errorMessage}
      />

      <ThingsToKnowModal modalProps={thingsToKnowModalProps} />
    </div>
  );
};
